/**
 * setting.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { StorageService } from "../support/APIs/storage.service";
import { BuildFlags } from "./build-flags";
import { ConfigurationService } from "./configuration.service";
import { Setting, MapSetting } from "./setting";

/**
 * {@link SettingService} provides a central interface for reading user
 * {@link Setting}s, which are customizable and whose values are cached
 * in-browser, when storage is available via the {@link StorageService}.
 */
@Injectable()
export class SettingService
{
  /**
   * The {@link Setting}s that have been read from defaults or storage.
   * NOTE: this will *usually* contain all setting keys that can be used,
   * however nothing precludes the application from creating a dynamic setting
   * that doesn't have a default value in a config file and which only gets
   * created lazily. (That said, this is probably bad UX because one could
   * visit the settings page before the setting was initialized...)
   * @type {Map<string, Setting>}
   */
  public settings : Map<string, Setting> = new Map<string, Setting>();

  public readonly mapSettings : MapSetting[] = [];

  /**
   * Create the {@link SettingService} and read defaults from the config and
   * then the user's local storage.
   * @param  {StorageService} storage DI
   * @return {SettingService}
   */
  constructor (
    protected storage : StorageService,
    protected config : ConfigurationService,
    private sanitizer : DomSanitizer
  )
  {
    this.readDefaults([this.config.settings.defaults]);
    this.readMapDefaults(this.config.settings.mapDefaults);
  }

  /**
   * Extract the {@link Setting} objects from defaults arrays and store them to
   * the map with updated values.
   * @param {Setting[][]} configs array of default settings arrays
   */
  private readDefaults (configs : Setting[][]) : void
  {
    configs.forEach((defaults : Setting[]) => {
      defaults.forEach((setting : Setting) =>
        this.add(setting));
    });
  }

  private readMapDefaults (defaults : MapSetting[]) : void
  {
    defaults.forEach((setting : MapSetting) => {
      this.add(setting, true);
    });
  }

  public isMapSetting (setting : Setting) : setting is MapSetting
  {
    // duck-type by presence of keyType field
    return "keyType" in setting;
  }

  /**
   * Add an entirely new setting, and load the user's value for it, if any.
   * @param {Setting} setting The new setting to store
   */
  private add (setting : Setting, isMap : boolean = false) : void
  {
    if (typeof setting.presentation === "string")
    {
      setting.presentationUnsafe = this.sanitizer.bypassSecurityTrustHtml(setting.presentation);
    }
    this.settings.set(setting.name, setting);
    this.load(setting.name);
    if (isMap)
    {
      this.mapSettings.push(setting as MapSetting);
    }
  }

  /**
   * Verify the map contains a setting with a particular name
   * @param  {string}  name
   * @return {boolean}
   */
  private has (name : string) : boolean
  {
    return this.settings.has(name);
  }

  /**
   * Check for a value in browser storage, and update the map accordingly.
   * @param {string} name
   */
  private load (name : string) : void
  {
    const item : any = this.storage.get(name);
    if (item !== null)
    {
      if (!this.settings.has(name))
      {
        throw new Error(name + " present in storage, but not in app settings");
      }
      const setting : Setting = this.settings.get(name);
      if (!BuildFlags.debugMode && setting.debugOnly)
      {
        // We do not allow debugOnly settings to be loaded outside of debug mode
        return;
      }
      setting.value = item;
    }
  }

  /**
   * Store the current value for a key in the browser storage.
   * @param {string} name
   */
  private save (name : string) : void
  {
    this.storage.set(name, this.settings.get(name).value);
  }

  /**
   * Retrieve the value of a {@link Setting} by its name
   * @param  {string} name
   * @return {any}
   */
  public get (name : string) : any
  {
    this.load(name);
    return this.settings.get(name).value;
  }

  public getOr (name : string, fallback : any) : any
  {
    if (this.has(name))
    {
      return this.get(name);
    }
    else
    {
      return fallback;
    }
  }

  /**
   * Retrieve the a {@link Setting} object by its name
   * @param  {string} name
   * @return {any}
   */
  public getSetting (name : string) : Setting
  {
    this.load(name);
    return this.settings.get(name);
  }

  /**
   * Set the value of a {@link Setting} by its name
   * @param {string}     name
   * @param {any}        value
   * @param {boolean}    save  Whether to propagate the value to browser storage
   */
  public set (name : string, value : any, save : boolean = true) : void
  {
    console.log("Setting value for " + name, value);
    if (!this.has(name))
    {
      throw new Error("No local setting with name '" + name + "'");
    }
    this.settings.get(name).value = value;
    if (save)
    {
      this.save(name);
    }
  }

  /**
   * Retrieve an array of all the {@link Setting}s in the map
   * @return {Setting[]}
   */
  public all () : Setting[]
  {
    return Array.from(this.settings.values());
  }

  /**
   * Retrieve an array of {@link Setting}s visible in the current mode.
   * @return {Setting[]}
   */
  public visible () : Setting[]
  {
    if (BuildFlags.debugMode)
    {
      return this.all();
    }
    else
    {
      return this.all().filter((s : Setting) => !s.debugOnly);
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/configuration/setting.service.ts